import React, { useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { AuthPageLayout } from '../shared/components/containers/AuthPageLayout';
import { Form } from 'react-bootstrap';
import { PasswordInput } from '../shared/components/inputs/PasswordInput';
import { PrimaryButton } from '../shared/components/buttons/PrimaryButton';
import { BasicAnchor } from '../shared/components/links/BasicAnchor';
import { BrandHeader } from '../shared/components/headers/BrandHeader';
import { ApiError } from '../api/client';
import { submitNewPassword } from '../api/auth';

interface OwnProps {
}

const StyledPasswordInput = styled(PasswordInput)`
  width: 100%;
  max-width: 350px;
  margin-bottom: 20px;
`;

const InFormHeader = styled.div`
  font-weight: 600;
  font-size: 1.3em;
  margin-bottom: 5px;
`;

const HeaderExplanation = styled(Form.Text)`
  margin-bottom: 15px;
  display: flex;
`;

const ButtonRow = styled.div`
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginRow = styled.div`
  margin-top: 30px;
`;

const ErrorText = styled(Form.Text)`
  color: ${(props) => props.theme.colors.error};
  display: block;
  margin-bottom: 15px;
`;

const ConfirmationText = styled(Form.Text)`
  display: block;
  margin-bottom: 15px;
`;

const ResetPasswordPage = (props: OwnProps) => {
  const {} = props;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const resetPasswordToken = searchParams.get('reset_password_token');

  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmitClick = async () => {
    if (password !== passwordConfirm) {
      setError(t('resetPassword.errors.passwordMismatch'));
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await submitNewPassword(resetPasswordToken ?? '', password, passwordConfirm);
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('resetPassword.errors.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthPageLayout>
      <BrandHeader/>

      <InFormHeader>{t('resetPassword.heading')}</InFormHeader>

      {!resetPasswordToken && !done && (
        <ErrorText>
          {t('resetPassword.invalidLink')}
        </ErrorText>
      )}

      {done ? (
        <ConfirmationText>
          <Trans i18nKey="resetPassword.confirmation">
            Your password has been reset. <BasicAnchor url='/'>Log in</BasicAnchor> with your new password.
          </Trans>
        </ConfirmationText>
      ) : (
        <>
          <HeaderExplanation muted>{t('resetPassword.explanation')}</HeaderExplanation>

          <Form.Label htmlFor="reset-password-new">{t('resetPassword.newPasswordLabel')}</Form.Label>
          <StyledPasswordInput id="reset-password-new" onChange={setPassword} value={password}/>

          <Form.Label htmlFor="reset-password-confirm">{t('resetPassword.confirmPasswordLabel')}</Form.Label>
          <StyledPasswordInput id="reset-password-confirm" onChange={setPasswordConfirm} value={passwordConfirm}/>

          {error && <ErrorText>{error}</ErrorText>}

          <ButtonRow>
            <PrimaryButton onClick={onSubmitClick} disabled={submitting || !resetPasswordToken}>
              {submitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
            </PrimaryButton>
          </ButtonRow>
        </>
      )}

      <LoginRow>
        <Form.Text muted>
          <BasicAnchor url='/'>{t('resetPassword.backToLogin')}</BasicAnchor>
        </Form.Text>
      </LoginRow>
    </AuthPageLayout>
  );
};

export {
  ResetPasswordPage,
};
